import { gql, useQuery } from '@apollo/client'

import { useGetSearchResultsData } from '@/hooks/srp/useGetSearchResultsData'

// Apollo query to check if the search results page URL contains the query parameter search=true.
const SEARCH_CLIENT_QUERY = gql`
  query SearchClientData {
    searchClient {
      isDomCat
      searchValue
    }
  }
`

/**
 * Custom hook for determining whether SRP (search results page) ads should be shown.
 * Ads are removed if the toggle RemoteParamKeys.SEO_SEARCH_LANDING_PAGES is enabled and
 * we're in a keyword search SRP (i.e. search=true param).
 * Ads are always shown for radial search, regardless of other conditions.
 *
 * @param categoryId - The ID of the category to check.
 * @param isRadialSearch - Whether the current search is a radial search
 * @returns An object containing a boolean `shouldShowSrpAds` indicating whether SRP ads should be shown.
 *
 * @example
 * const { shouldShowSrpAds } = useShouldShowSrpAds(false);
 * if (shouldShowSrpAds) {
 *   // Show ads
 * }
 */
export const useShouldShowSrpAds = (isRadialSearch: boolean) => {
  const { data } = useGetSearchResultsData()
  const { searchQuery } = data || {}
  const keyword = String(searchQuery?.keywords)

  const { data: searchClientData } = useQuery(SEARCH_CLIENT_QUERY)

  const isDomCat = searchClientData?.searchClient?.isDomCat === true
  const isSearchTrue = searchClientData?.searchClient?.searchValue === true
  const hasKeyword = typeof keyword === 'string' && keyword.trim() !== ''

  // Always show ads for domestic categories or radial searches
  if (isDomCat || isRadialSearch) {
    return { shouldShowSrpAds: true }
  }

  // If we don't have a keyword and search is not true, show ads (BRPs)
  if (!hasKeyword && !isSearchTrue) {
    return { shouldShowSrpAds: true }
  }

  // If we have a keyword and search is true, show ads
  if (hasKeyword && isSearchTrue) {
    return { shouldShowSrpAds: true }
  }

  // Otherwise, don't show ads
  return { shouldShowSrpAds: false }
}
